import { XLSX_FORMAT } from "@/model/constants"
import { Template } from "@/model/interfaces/form/template/template"
import { Pagination } from "@/model/interfaces/pagination"
import { SurveySubmission } from "@/model/interfaces/submission/surveySubmission/surveySubmission"
import SurveySubmissionFilter from "@/model/interfaces/submission/surveySubmission/surveySubmissionFilter"
import { TemplateSubmission } from "@/model/interfaces/submission/templateSubmission/templateSubmission"
import SurveySubmissionFilterService from "@/services/submission/surveySubmission/SurveySubmissionFilterService"
import TemplateSubmissionService from "@/services/submission/templateSubmission/TemplateSubmissionService"
import DocumentUtils from "@/services/utils/DocumentUtils"
import PaginationUtils from "@/services/utils/PaginationUtils"
import axios from "axios"
import { Vue } from "vue-property-decorator"

export default class SurveySubmissionService {
  private static URI = "survey_submissions"

  static async getByTemplateSubmission(templateSubmission: TemplateSubmission, filters: SurveySubmissionFilter): Promise<{ submissions: SurveySubmission[]; totalItems: number }> {
    const response = await Vue.$axios.get(TemplateSubmissionService.URI + "/" + templateSubmission.id + "/" + this.URI, {
      params: {
        ...SurveySubmissionFilterService.normalizeFilters(filters)
      }
    })

    return {
      submissions: response.data["hydra:member"],
      totalItems: response.data["hydra:totalItems"]
    }
  }

  static async exportByTemplateSubmission(templateSubmission: TemplateSubmission, filters: SurveySubmissionFilter, isHarmonizedData = false, password = ""): Promise<boolean> {
    SurveySubmissionFilterService.setFieldsType(isHarmonizedData, filters)
    const url = new URL(process.env.VUE_APP_API_URL + TemplateSubmissionService.URI + "/" + templateSubmission.id + "/" + this.URI + "/export")

    if (password !== "") {
      url.searchParams.append("password", password)
    }

    const response = await Vue.$axios.get(url.href, {
      params: {
        ...SurveySubmissionFilterService.normalizeFilters(filters)
      }
    })

    return response.status === 202
  }

  static async getSampleProfileTable(templateSubmission: TemplateSubmission): Promise<string> {
    const response = await Vue.$axios.get(TemplateSubmissionService.URI + "/" + templateSubmission.id + "/sample_profile", {})
    return response.data
  }

  static async getByTemplate(template: Template, filters: SurveySubmissionFilter, pagination: Pagination): Promise<{ submissions: SurveySubmission[]; pagination: Pagination }> {
    filters["templateSubmission.template.uid"] = template.uid
    const response = await Vue.$axios.get(this.URI, {
      params: {
        ...SurveySubmissionFilterService.normalizeFilters(filters),
        ...PaginationUtils.normalizePagination(pagination)
      }
    })

    const { current, last } = PaginationUtils.parseCurrentLast(response)

    return {
      submissions: response.data["hydra:member"],
      pagination: {
        current: current,
        itemsPerPage: pagination.itemsPerPage,
        last: last,
        itemCount: response.data["hydra:totalItems"]
      }
    }
  }

  static async exportByTemplate(template: Template, filters: SurveySubmissionFilter, isAsync = false): Promise<boolean | void> {
    SurveySubmissionFilterService.setFieldsType(true, filters)
    delete filters.search
    filters["templateSubmission.template.uid"] = template.uid

    let config: any = {
      params: {
        ...SurveySubmissionFilterService.normalizeFilters(filters)
      }
    }
    config = isAsync ? { ...config } : { responseType: "blob", ...config }

    const url = this.URI + (isAsync ? "/export" : XLSX_FORMAT)

    const response = await Vue.$axios.get(url, config)

    if (isAsync) {
      return response.status === 202
    }

    DocumentUtils.createXlsx(response.data)
  }

  static async patchSubmissions(submissionsToUpdate: Partial<SurveySubmission>[], progress: { (updated: number, total: number): void }): Promise<void> {
    const BATCH_SIZE = 10
    for (let i = 0; i < submissionsToUpdate.length; i += BATCH_SIZE) {
      const batch = submissionsToUpdate.slice(i, i + BATCH_SIZE)
      const requests = batch.map(surveySubmission => {
        return Vue.$axios.patch(this.URI + "/" + surveySubmission.id, surveySubmission)
      })

      try {
        progress(i, submissionsToUpdate.length)
        await axios.all(requests)
      } catch (error) {
        console.error("Error updating batch:", error)
      }
    }
  }
}
